import * as THREE from "three";

import { RAMPS, rampFor } from "./indicatorEngine";

/**
 * Couche "climat" : les variables du bilan hydrique (pluie, ETP, pluies
 * efficaces, infiltration, ruissellement, temperature, humidite des sols),
 * par mois et par horizon, interpolees en continu sur le relief a partir de
 * points de mesure.
 *
 * Partage entre le viewer de production (TerrainViewer) et le bac a sable
 * (terrain_test/TerrainTestPage).
 *
 * CE QUI A CHANGE. La version precedente DEVINAIT les axes en parsant des
 * noms de proprietes (`etp_2050_m01`) : le fichier etait un sac de cles
 * plates, et toute variable inconnue du front restait invisible. Le datapack
 * declare desormais ses axes explicitement (`axes.variables`, `axes.horizons`,
 * `axes.periods`) et range les valeurs en series de 13 nombres — l'annuel en
 * tete, puis les douze mois. Ajouter une variable ou un horizon a la source
 * suffit a le voir apparaitre dans les selecteurs, sans toucher a ce fichier.
 *
 * POURQUOI L'INTERPOLATION EST FAITE ICI, ET PAS DANS LE PIPELINE PYTHON
 * comme la geologie ou les communes : la source croise 7 variables x 3
 * horizons x 13 periodes, soit 273 combinaisons. Precalculer un raster par
 * combinaison ferait 273 x 375 000 cellules (plus de 100 Mo a telecharger)
 * pour une source qui tient en quelques centaines de Ko. On charge donc les
 * points bruts et on fabrique la nappe a la volee.
 *
 * POURQUOI CA RESTE INSTANTANE : interpoler directement les 375 000 sommets
 * du terrain depuis 88 points ferait 33 millions de distances a chaque
 * changement de mois. Un champ climatique etant lisse, on passe par une
 * grille intermediaire beaucoup plus lache (~2 km de maille, quelques
 * milliers de noeuds), dont les POIDS d'interpolation sont calcules une
 * seule fois au chargement. Changer de variable/horizon/mois se reduit
 * alors a une somme ponderee sur cette petite grille, puis a un simple
 * echantillonnage bilineaire vers les sommets du terrain.
 */

// Maille de la grille intermediaire, en metres. 2 km : nettement plus fin
// que l'espacement moyen des points de mesure (~13 km sur l'Ardeche), donc
// la grille ne perd rien du signal, tout en restant ~100x plus legere que
// la grille du terrain.
const LATTICE_STEP_M = 2000;

// Ponderation par l'inverse de la distance au carre (Shepard).
const IDW_POWER = 2;

// LISSAGE — le parametre qui fait disparaitre les "ronds" autour des points.
//
// L'IDW nu est un interpolateur EXACT : quand un noeud s'approche d'une
// station, son poids tend vers 1 et la nappe colle a la valeur mesuree. Le
// resultat est un disque de couleur centre sur chaque station, puis un
// aplat entre les stations — l'artefact classique dit "oeil de boeuf". Le
// borner par le bas (l'ancienne `IDW_MIN_DISTANCE_M`) ne faisait que
// plafonner le pic sans le supprimer.
//
// Ajouter un terme constant s au carre de la distance, w = 1/(d^2 + s^2)^(p/2),
// rend l'interpolateur INEXACT : plus aucun noeud ne peut atteindre le poids
// 1, la nappe ne passe plus exactement par les points, et les disques
// s'effacent. s est homogene a une distance : c'est le rayon en deca duquel
// une station cesse de dominer ses voisines. Un ordre de grandeur de la
// moitie de l'espacement moyen des stations (~13 km ici, donc ~6 km) donne
// un champ lisse sans effacer les contrastes reels.
export const CLIMATE_DEFAULT_SMOOTHING_M = 6000;

// Garde-fou quand le lissage est a zero : sans lui, un noeud tombant pile sur
// une station diviserait par zero.
const IDW_EPSILON_M = 50;

export const CLIMATE_PERIODS = [
  "annuel", "m01", "m02", "m03", "m04", "m05", "m06",
  "m07", "m08", "m09", "m10", "m11", "m12",
];

const PERIOD_LABELS = {
  annuel: "Année entière",
  m01: "Janvier", m02: "Février", m03: "Mars", m04: "Avril",
  m05: "Mai", m06: "Juin", m07: "Juillet", m08: "Août",
  m09: "Septembre", m10: "Octobre", m11: "Novembre", m12: "Décembre",
};

const MONTH_SHORT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export const CLIMATE_MONTHS = CLIMATE_PERIODS.slice(1);
export const CLIMATE_MONTH_LABELS = MONTH_SHORT;

export function periodLabel(period) {
  return PERIOD_LABELS[period] || period;
}

export function horizonLabel(horizon) {
  return horizon === "actuel" ? "Actuel" : horizon;
}

/**
 * Position d'une periode dans une serie : 0 = annuel, 1 a 12 = janvier a
 * decembre. L'annuel est range EN TETE de la serie plutot que dans un champ
 * separe, ce qui permet d'indexer une periode sans distinguer deux cas.
 */
export function periodIndex(period) {
  const i = CLIMATE_PERIODS.indexOf(period);
  return i < 0 ? 0 : i;
}

export const CLIMATE_DEFAULT_OPACITY = 0.55;
export const CLIMATE_MIN_OPACITY = 0.1;
export const CLIMATE_MAX_OPACITY = 1;

// Decollage de la nappe au-dessus du terrain, en unites de scene et APRES
// exageration (meme convention que pluviometrie, geologie, communes).
export const CLIMATE_Y_OFFSET = 28;

// Grandeurs de synthese affichees dans la fiche d'un point de mesure. Elles
// resument un bilan annuel : elles ne se cartographient pas en nappe, mais
// c'est la qu'on lit ou part reellement l'eau.
export const CLIMATE_SCALARS = [
  { id: "bilan_hydrique.deficit_climatique_mm", label: "Déficit climatique", unit: "mm",
    help: "Somme, mois par mois, de ce que l'ETP dépasse la pluie. Cumulé à l'année, l'excédent d'hiver masquerait le manque d'été." },
  { id: "bilan_hydrique.nb_mois_deficitaires", label: "Mois déficitaires", unit: "mois" },
  { id: "bilan_hydrique.ecoulement_total_mm", label: "Écoulement total", unit: "mm" },
  { id: "bilan_hydrique.part_infiltration_pct", label: "Part infiltrée", unit: "%",
    help: "Sur ce qui quitte réellement le sol : infiltration + ruissellement." },
  { id: "secheresse_sol.nb_mois_sous_seuil", label: "Mois à sol sec", unit: "mois",
    help: "Mois dont l'indice d'humidité des sols passe sous 0,4." },
  { id: "temperature.amplitude_annuelle_c", label: "Amplitude annuelle", unit: "°C" },
];

/**
 * Axes REELLEMENT presents dans le datapack. Les rampes sont resolues ici,
 * une fois : chaque variable repart avec le tableau de couleurs de sa rampe,
 * ce qui evite de rechercher la palette a chaque changement de mois et fait
 * fonctionner le cache de LUT plus bas (identite du tableau).
 */
export function parseClimateAxes(climate) {
  const axes = climate?.axes;
  if (!axes) return { variables: [], horizons: [], periods: [] };
  return {
    variables: (axes.variables || []).map((variable) => ({
      ...variable,
      rampId: variable.ramp,
      ramp: RAMPS[variable.ramp] || rampFor(variable),
    })),
    horizons: (axes.horizons || []).map((h) => (typeof h === "string" ? h : h.id)),
    horizonYears: Object.fromEntries(
      (axes.horizons || [])
        .filter((h) => typeof h !== "string")
        .map((h) => [h.id, h.annee])
    ),
    periods: (axes.periods || CLIMATE_PERIODS).filter((p) => CLIMATE_PERIODS.includes(p)),
  };
}

/** Valeur d'une variable en un point, pour un horizon et une periode. */
export function climateValueAt(point, variable, horizon, period) {
  const series = point?.series?.[variable.id]?.[horizon];
  if (!series) return null;
  const value = series[periodIndex(period)];
  return typeof value === "number" ? value : null;
}

export function climateValues(points, variable, horizon, period) {
  const index = periodIndex(period);
  return Float32Array.from(points, (point) => {
    const series = point?.series?.[variable.id]?.[horizon];
    const value = series ? series[index] : null;
    return typeof value === "number" ? value : NaN;
  });
}

/**
 * Bornes de l'echelle de couleur, calculees sur TOUS les horizons de la
 * periode affichee. C'est ce qui rend la comparaison possible : a echelle
 * recalculee par horizon, la carte 2050 serait le decalque de la carte
 * actuelle et le changement climatique deviendrait invisible. L'echelle
 * reste en revanche propre a chaque periode, sinon janvier et juillet
 * seraient deux aplats uniformes aux deux bouts de la rampe.
 */
export function climateDomain(points, variable, period, horizons) {
  const index = periodIndex(period);
  let min = Infinity;
  let max = -Infinity;
  (horizons || []).forEach((horizon) => {
    (points || []).forEach((point) => {
      const series = point?.series?.[variable.id]?.[horizon];
      const value = series ? series[index] : null;
      if (typeof value !== "number" || !Number.isFinite(value)) return;
      if (value < min) min = value;
      if (value > max) max = value;
    });
  });
  if (min > max) return { min: 0, max: 1 };
  return { min, max };
}

function rampRgb(t, ramp) {
  const clamped = Math.min(1, Math.max(0, t));
  for (let i = 0; i < ramp.length - 1; i++) {
    const [t0, c0] = ramp[i];
    const [t1, c1] = ramp[i + 1];
    if (clamped >= t0 && clamped <= t1) {
      const f = (clamped - t0) / (t1 - t0 || 1);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return ramp[ramp.length - 1][1];
}

/**
 * Rampe echantillonnee en 256 teintes deja converties en espace lineaire.
 * Colorer 375 000 sommets appelle sinon autant de conversions sRGB (trois
 * puissances par sommet) a chaque changement de mois ; ici c'est 256
 * conversions une fois pour toutes, puis une simple recopie.
 */
function buildRampLut(ramp) {
  const lut = new Float32Array(256 * 3);
  const color = new THREE.Color();
  for (let i = 0; i < 256; i++) {
    const [r, g, b] = rampRgb(i / 255, ramp);
    color.setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
    lut[i * 3] = color.r;
    lut[i * 3 + 1] = color.g;
    lut[i * 3 + 2] = color.b;
  }
  return lut;
}

const rampLutCache = new Map();
function rampLut(ramp) {
  let lut = rampLutCache.get(ramp);
  if (!lut) {
    lut = buildRampLut(ramp);
    rampLutCache.set(ramp, lut);
  }
  return lut;
}

/** Couleur d'une valeur isolee (marqueur de station), meme rampe que la nappe. */
export function climateColor(value, domain, ramp) {
  const t = (value - domain.min) / (domain.max - domain.min || 1);
  const [r, g, b] = rampRgb(t, ramp);
  return new THREE.Color().setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
}

/**
 * Prepare l'interpolation : calcule une fois les poids de chaque noeud de la
 * grille intermediaire vis-a-vis des points de mesure, puis expose
 * `fillColors` qui teinte les sommets du terrain pour un jeu de valeurs.
 *
 * `points` porte des coordonnees Lambert93 (x, y), converties ici en
 * coordonnees locales de la scene, comme le reste des calques.
 *
 * `smoothingM` (cf. CLIMATE_DEFAULT_SMOOTHING_M) se change a chaud via
 * `setSmoothing` : seuls les POIDS sont recalcules, soit ici ~3 900 noeuds x
 * 88 points. C'est assez leger pour suivre un curseur en temps reel, ce qui
 * est exactement ce qu'il faut pour regler le lissage a l'oeil.
 */
export function createClimateInterpolator({
  points, cols, rows, cellsize, originX, originY,
  smoothingM = CLIMATE_DEFAULT_SMOOTHING_M,
}) {
  const halfW = ((cols - 1) * cellsize) / 2;
  const halfH = ((rows - 1) * cellsize) / 2;
  const northY = originY + rows * cellsize;
  const worldW = (cols - 1) * cellsize;
  const worldH = (rows - 1) * cellsize;

  const localX = points.map((p) => p.x - originX - halfW);
  const localZ = points.map((p) => northY - p.y - halfH);

  const latticeCols = Math.floor(worldW / LATTICE_STEP_M) + 1;
  const latticeRows = Math.floor(worldH / LATTICE_STEP_M) + 1;
  const nodeCount = latticeCols * latticeRows;
  const pointCount = points.length;

  // Poids normalises, un bloc de `pointCount` valeurs par noeud.
  const weights = new Float32Array(nodeCount * pointCount);
  let currentSmoothingM = null;

  function computeWeights(smoothing) {
    // Le terme de lissage s'ajoute au CARRE de la distance : c'est ce qui
    // empeche un noeud proche d'une station d'atteindre le poids 1, donc ce
    // qui supprime le disque de couleur autour d'elle.
    const softeningSq = Math.max(smoothing, 0) ** 2 + IDW_EPSILON_M * IDW_EPSILON_M;
    for (let lr = 0; lr < latticeRows; lr++) {
      for (let lc = 0; lc < latticeCols; lc++) {
        const nx = lc * LATTICE_STEP_M - halfW;
        const nz = lr * LATTICE_STEP_M - halfH;
        const base = (lr * latticeCols + lc) * pointCount;
        let total = 0;
        for (let i = 0; i < pointCount; i++) {
          const dx = nx - localX[i];
          const dz = nz - localZ[i];
          const w = 1 / Math.pow(dx * dx + dz * dz + softeningSq, IDW_POWER / 2);
          weights[base + i] = w;
          total += w;
        }
        for (let i = 0; i < pointCount; i++) weights[base + i] /= total;
      }
    }
    currentSmoothingM = smoothing;
  }

  computeWeights(smoothingM);

  const lattice = new Float32Array(nodeCount);
  // Facteurs constants du passage grille intermediaire -> sommets du terrain.
  const uPerCol = cellsize / LATTICE_STEP_M;

  return {
    latticeCols,
    latticeRows,
    /**
     * Change le lissage. Sans effet (et sans recalcul) si la valeur est
     * inchangee : le curseur emet un evenement par pixel parcouru, la
     * plupart pour la meme valeur arrondie.
     */
    setSmoothing(nextSmoothingM) {
      if (nextSmoothingM === currentSmoothingM) return false;
      computeWeights(nextSmoothingM);
      return true;
    },
    /**
     * Ecrit les couleurs de la nappe (3 floats par sommet du terrain) pour
     * le jeu de valeurs `values` (une par point de mesure).
     */
    fillColors(target, values, domain, ramp) {
      for (let node = 0; node < nodeCount; node++) {
        const base = node * pointCount;
        let sum = 0;
        for (let i = 0; i < pointCount; i++) sum += weights[base + i] * values[i];
        lattice[node] = sum;
      }

      const lut = rampLut(ramp);
      const span = domain.max - domain.min || 1;
      for (let r = 0; r < rows; r++) {
        const v = Math.min(r * uPerCol, latticeRows - 1.0001);
        const lr0 = Math.floor(v);
        const fy = v - lr0;
        const rowBase0 = lr0 * latticeCols;
        const rowBase1 = Math.min(lr0 + 1, latticeRows - 1) * latticeCols;
        for (let c = 0; c < cols; c++) {
          const u = Math.min(c * uPerCol, latticeCols - 1.0001);
          const lc0 = Math.floor(u);
          const fx = u - lc0;
          const lc1 = Math.min(lc0 + 1, latticeCols - 1);
          const value =
            lattice[rowBase0 + lc0] * (1 - fx) * (1 - fy) +
            lattice[rowBase0 + lc1] * fx * (1 - fy) +
            lattice[rowBase1 + lc0] * (1 - fx) * fy +
            lattice[rowBase1 + lc1] * fx * fy;

          let shade = Math.round(((value - domain.min) / span) * 255);
          shade = shade < 0 ? 0 : shade > 255 ? 255 : shade;
          const from = shade * 3;
          const to = (r * cols + c) * 3;
          target[to] = lut[from];
          target[to + 1] = lut[from + 1];
          target[to + 2] = lut[from + 2];
        }
      }
    },
  };
}

/**
 * Marqueurs des points de mesure : un InstancedMesh (les 88 stations en un
 * seul draw call) double d'un mesh de clic invisible plus large — une
 * pastille de quelques centaines de metres est invisable a l'echelle d'un
 * departement. Meme schema que les prelevements.
 */
export function createClimateStations({ points, sampleAt, worldDiag, zRange, exaggeration }) {
  const meta = [];
  const placed = [];
  points.forEach((point) => {
    const sample = sampleAt(point.x, point.y);
    if (sample) placed.push({ point, sample });
  });
  if (!placed.length) return null;

  const radius = Math.max(worldDiag * 0.0016, 200);
  const height = Math.max(zRange * 0.08, 150);
  const hitRadius = Math.max(radius * 3.5, worldDiag * 0.001);

  // Legerement conique (large en haut) : la pastille reste lisible vue de
  // dessus sans grossir l'empreinte au sol.
  const geometry = new THREE.CylinderGeometry(1, 0.55, 1, 10);
  const mesh = new THREE.InstancedMesh(
    geometry,
    new THREE.MeshStandardMaterial({ roughness: 0.45, metalness: 0 }),
    placed.length
  );
  mesh.castShadow = true;
  const hitMesh = new THREE.InstancedMesh(
    geometry,
    new THREE.MeshBasicMaterial({ visible: false }),
    placed.length
  );

  const dummy = new THREE.Object3D();
  placed.forEach(({ point, sample }, index) => {
    meta.push({
      index,
      data: point,
      x: sample.x,
      z: sample.z,
      rawH: sample.rawH,
      radius,
      height,
      hitRadius,
      baseColor: new THREE.Color("#ffffff"),
    });
  });
  meta.forEach((station) => {
    applyClimateStationTransform(mesh, dummy, station, exaggeration, station.radius);
    applyClimateStationTransform(hitMesh, dummy, station, exaggeration, station.hitRadius);
  });
  mesh.instanceMatrix.needsUpdate = true;
  hitMesh.instanceMatrix.needsUpdate = true;

  return { mesh, hitMesh, meta, dummy };
}

export function applyClimateStationTransform(mesh, dummy, station, exaggeration, radius) {
  station.currentY = station.rawH * exaggeration;
  dummy.position.set(station.x, station.currentY + station.height / 2, station.z);
  dummy.scale.set(radius, station.height, radius);
  dummy.updateMatrix();
  mesh.setMatrixAt(station.index, dummy.matrix);
}

/** Recolore les marqueurs quand la variable/l'horizon/la periode change. */
export function paintClimateStations(stations, variable, horizon, period, domain) {
  stations.meta.forEach((station) => {
    const value = climateValueAt(station.data, variable, horizon, period);
    station.baseColor = typeof value === "number"
      ? climateColor(value, domain, variable.ramp)
      : new THREE.Color("#9a9a9a");
    stations.mesh.setColorAt(station.index, station.baseColor);
  });
  if (stations.mesh.instanceColor) stations.mesh.instanceColor.needsUpdate = true;
}

export function formatClimateValue(variable, value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "n/d";
  // Une temperature ou un indice d'humidite se jouent a la decimale, un
  // cumul de precipitations non : le nombre de decimales suit l'ordre de
  // grandeur plutot qu'un reglage par variable.
  const digits = Math.abs(value) < 10 ? 2 : 0;
  const formatted = value.toLocaleString("fr-FR", { maximumFractionDigits: digits });
  return variable.unit ? `${formatted} ${variable.unit}` : formatted;
}

/** Series mensuelles d'une station, un jeu par horizon (graphe de la fiche). */
export function stationMonthlySeries(station, variable, horizons) {
  return (horizons || []).map((horizon) => {
    const series = station?.series?.[variable.id]?.[horizon];
    return {
      horizon,
      // Index 0 = annuel : le profil mensuel commence a l'index 1.
      values: series ? series.slice(1) : CLIMATE_MONTHS.map(() => null),
      annual: series ? series[0] : null,
    };
  });
}

/**
 * Grandeurs de synthese d'une station pour un horizon, prêtes a afficher.
 * Seules celles reellement presentes sont renvoyees : l'export ne porte pas
 * les memes blocs pour tous les territoires.
 */
export function stationScalars(station, horizon) {
  const scalars = station?.scalars?.[horizon];
  if (!scalars) return [];
  return CLIMATE_SCALARS
    .filter((scalar) => typeof scalars[scalar.id] === "number")
    .map((scalar) => ({
      ...scalar,
      value: scalars[scalar.id],
      text: `${scalars[scalar.id].toLocaleString("fr-FR", { maximumFractionDigits: 1 })}${scalar.unit ? ` ${scalar.unit}` : ""}`,
    }));
}

// Teintes des courbes de la fiche station : l'actuel en neutre clair, les
// horizons futurs de plus en plus chauds — on doit voir d'un coup d'oeil
// dans quel sens le climat se deplace.
export const HORIZON_COLORS = ["#b7e0e1", "#e0ac3c", "#e2694f", "#a173ad"];
